// Funzioni di utilità per la gestione delle gare individuali
import { generateCompleteRoundRobin } from './roundRobinScheduler';

/**
 * Genera tutti gli incontri round robin tra i partecipanti
 * @param {string[]} participants - lista nomi
 * @returns {Array<{A: string, B: string, id: string, completed: boolean, scoreA?: number, scoreB?: number}>}
 */
export function generateIndividualRoundRobin(participants) {
  const matches = [];
  for (let i = 0; i < participants.length; i++) {
    for (let j = i + 1; j < participants.length; j++) {
      matches.push({
        A: participants[i],
        B: participants[j],
        id: `match_${i}_${j}_${Date.now()}_${Math.floor(Math.random() * 10000)}`,
        completed: false
      });
    }
  }
  return matches;
}

/**
 * Genera teams e matches round robin compatibili con la logica squadre (teamA/teamB come indice)
 * @param {string[]} participants
 * @returns {{ teams: Array<{name:string, members: string[]}>, matches: Array<{teamA:number, teamB:number, id:string, completed:boolean}> }}
 */
export function generateIndexedIndividualRoundRobin(participants) {
  const teams = participants.map(name => ({ name, members: [name] }));
  const matches = [];
  for (let i = 0; i < participants.length; i++) {
    for (let j = i + 1; j < participants.length; j++) {
      matches.push({
        teamA: i,
        teamB: j,
        id: `match_${i}_${j}_${Date.now()}_${Math.floor(Math.random() * 10000)}`,
        completed: false
      });
    }
  }
  return { teams, matches };
}

/**
 * Divide una lista di atleti in n gironi (array di array)
 * @param {string[]|string[][]} participants
 * @param {number} numPools
 * @returns {string[][]} Array di gironi
 */
export function splitIntoPools(participants, numPools) {
  console.log("SPLIT INTO POOLS - Input:", participants, "numPools:", numPools);

  // Caso particolare: partecipanti è già un array di array (es. gironi manuali)
  if (Array.isArray(participants) && participants.length > 0 && Array.isArray(participants[0])) {
    console.log("Partecipanti già in formato array di array:", participants);
    return participants;
  }

  // Protezione input
  if (!Array.isArray(participants)) {
    console.error("Errore: participants non è un array:", participants);
    participants = [];
  }

  // Assicurati che numPools sia un numero valido
  numPools = Math.max(1, numPools || 1);

  // Crea array vuoti per ogni girone
  const pools = Array.from({ length: numPools }, () => []);

  // Distribuisci i partecipanti nei gironi
  participants.forEach((name, idx) => {
    // Includi tutti i partecipanti nei gironi, anche quelli vuoti
    // Ma per visualizzazione, meglio usare il nome originale invece di filtrare
    pools[idx % numPools].push(name);
  });

  // Non filtriamo più i gironi vuoti, per mantenere il numero corretto di gironi
  const nonEmptyPools = pools;

  // Se non ci sono gironi validi, crea almeno uno vuoto
  if (nonEmptyPools.length === 0) {
    nonEmptyPools.push([]);
  }

  console.log("SPLIT INTO POOLS - Output:", nonEmptyPools);
  return nonEmptyPools;
}

/**
 * Genera teams e matches per tutti i gironi - VERSIONE STABILE BASATA SUI NOMI
 * @param {string[]|string[][]} participants
 * @param {number} numPools
 * @returns {{ allTeams: Array<{name:string, pool:number, members: string[]}>, allMatches: Array<{teamA:number, teamB:number, pool:number, id:string, completed:boolean}> }}
 */
export function generatePoolsAndMatches(participants, numPools) {
  console.log("GENERATE POOLS AND MATCHES (VERSIONE STABILE) - Input:", participants, "numPools:", numPools);

  // Genera i gironi
  const pools = splitIntoPools(participants, numPools);
  console.log("Pools generati:", pools);

  // Ottieni lo schema di distribuzione da localStorage
  const schema = localStorage.getItem('individual_schema') || 'auto';
  console.log("Schema di distribuzione:", schema);

  let allTeams = [];
  let allMatches = [];
  let globalIdx = 0;

  // Dizionario nomi -> indice globale per riferimenti stabili
  const nameToIndex = {};

  // Per ogni girone, genera team e match
  pools.forEach((pool, poolIdx) => {
    // Filtriamo solo i partecipanti con nomi validi
    const validParticipants = pool.filter(name => name && name.trim() !== '');

    // Ogni atleta come team con members
    const poolTeams = validParticipants.map((name, idx) => {
      // Usa un nome valido o genera un placeholder
      const validName = name && name.trim() ? name.trim() : `Atleta ${globalIdx + idx + 1}`;

      // Memorizza la mappatura nome -> indice globale
      nameToIndex[validName] = globalIdx + idx;

      return {
        name: validName,
        pool: poolIdx,
        members: [validName], // Forza member come stringa
        // Memorizza il nome esplicito per fallback e debug
        _tiratore: validName
      };
    });

    console.log(`Teams generati per girone ${poolIdx}:`, poolTeams);

    // Genera match solo se ci sono almeno 2 partecipanti nel girone
    if (validParticipants.length >= 2) {
      console.log(`Generazione incontri per girone da ${validParticipants.length} tiratori utilizzando logica Round-Robin ottimizzata`);

      // Usa la funzione centralizzata che gestisce FIE (4-8) e Rondella/Berger (altri)
      const rrMatches = generateCompleteRoundRobin(validParticipants);

      rrMatches.forEach(rrMatch => {
        // Estrai nomi (rrMatch.fencerA/B sono oggetti {name: ...})
        const tiratoreA = rrMatch.fencerA.name;
        const tiratoreB = rrMatch.fencerB.name;

        // Trova gli indici globali corretti basati sulla mappatura creata dei poolTeams
        const indexA = nameToIndex[tiratoreA];
        const indexB = nameToIndex[tiratoreB];

        // Genera il match object standard
        const match = {
          teamA: indexA,
          teamB: indexB,
          pool: poolIdx,
          tiratorA: tiratoreA,
          tiratorB: tiratoreB,
          id: `match_${poolIdx}_${tiratoreA}_${tiratoreB}`,
          permanentId: `match_${[tiratoreA, tiratoreB].sort().join('_vs_')}`,
          completed: false
        };

        allMatches.push(match);
      });
    } else {
      console.log(`Girone ${poolIdx} ha meno di 2 partecipanti, nessun match generato`);
    }

    allTeams = allTeams.concat(poolTeams);
    globalIdx += poolTeams.length;
  });

  console.log("RISULTATO FINALE - Teams:", allTeams);
  console.log("RISULTATO FINALE - Matches:", allMatches);

  return { allTeams, allMatches, pools };
}
